/**
 * OAuth helper for Google / Microsoft sign-in — **full-page redirect** flow
 * (replaced the popup flow on 2026-06-23). The popup built its `redirect_uri`
 * from the build-time `VITE_BASE_URL`, so on the Cloudflare-Pages surfaces
 * (dev.edaptio.com / appnew.edaptio.com) the provider returned to a *different*
 * origin (stg/app.edaptio.com) and the opener could never read the token. The
 * redirect flow uses the **runtime origin** as the callback, so it always
 * returns to the same host the user started on.
 *
 * ⚠️ Each host's `/login` must be registered as an authorized redirect URI in
 * the Google OAuth client + the Azure app registration (e.g.
 * https://dev.edaptio.com/login, https://appnew.edaptio.com/login,
 * https://app.edaptio.com/login), or the provider rejects with
 * redirect_uri_mismatch.
 *
 * Flow:
 *   1. `this.$auth.authenticate(provider, {intent, returnTo, extra})` stashes
 *      {provider, state, intent, returnTo, extra} in sessionStorage and does a
 *      full-page navigation to the provider's authorize URL.
 *   2. The provider redirects back to `${origin}/login` with the token in the
 *      URL fragment (Google, implicit) or `?code=` (Microsoft, auth-code).
 *   3. On /login load, `this.$auth.handleRedirectResult()` verifies state,
 *      extracts/exchanges the token, clears the URL, and returns the result so
 *      the caller can complete login / signup / account-link.
 */
import axios from 'axios'

const PROVIDERS = {
    google: {
        clientId: () => import.meta.env.VITE_GOOGLE_CLIENT_ID,
        authorizationEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
        scope: ['openid', 'email', 'profile'],
        responseType: 'token', // implicit — token comes back in the URL fragment
    },
    microsoft: {
        clientId: () => import.meta.env.VITE_MICROSOFT_CLIENT_ID,
        authorizationEndpoint: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
        scope: ['https://graph.microsoft.com/user.read'],
        responseType: 'code', // auth-code — exchanged for a token at our API
        codeExchangeUrl: () => `${import.meta.env.VITE_BASE_API_URL}/auth/microsoft/login`,
        prompt: 'select_account',
    },
}

const PENDING_KEY = 'oauth_pending'

// Runtime origin's /login — matches wherever the SPA is served.
const REDIRECT_URI = () => `${window.location.origin}/login`

function generateState() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function buildAuthUrl(provider, state) {
    const cfg = PROVIDERS[provider]
    const params = new URLSearchParams({
        client_id: cfg.clientId(),
        redirect_uri: REDIRECT_URI(),
        response_type: cfg.responseType,
        scope: cfg.scope.join(' '),
        state,
    })
    if (cfg.prompt) params.set('prompt', cfg.prompt)
    return `${cfg.authorizationEndpoint}?${params.toString()}`
}

function parseHash() {
    const h = (window.location.hash || '').replace(/^#/, '')
    return Object.fromEntries(new URLSearchParams(h))
}

function parseQuery() {
    return Object.fromEntries(new URLSearchParams(window.location.search || ''))
}

function cleanUrl() {
    // Drop the OAuth fragment/query so a refresh can't replay the callback.
    window.history.replaceState({}, document.title, window.location.pathname)
}

/**
 * Start the OAuth flow as a full-page redirect. Does NOT resolve — the browser
 * navigates away to the provider. The flow is completed by
 * handleRedirectResult() after the provider returns to /login.
 *
 * @param {string} provider 'google' | 'microsoft'
 * @param {{intent?: string, returnTo?: string, extra?: object}} [opts]
 */
function authenticate(provider, opts = {}) {
    const cfg = PROVIDERS[provider]
    if (!cfg) throw new Error(`Unknown OAuth provider: ${provider}`)

    const state = generateState()
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        provider,
        state,
        intent: opts.intent || 'login',
        returnTo: opts.returnTo || null,
        extra: opts.extra || null,
    }))
    window.location.assign(buildAuthUrl(provider, state))
    return new Promise(() => {}) // page navigates away; never settles
}

/**
 * Complete an OAuth redirect. Call on /login load.
 * @returns {Promise<null | {provider, access_token, intent, returnTo, extra}>}
 *   null when there's no pending redirect to handle. Throws on provider error
 *   or state (CSRF) mismatch.
 */
async function handleRedirectResult() {
    const raw = sessionStorage.getItem(PENDING_KEY)
    const hash = parseHash()
    const query = parseQuery()
    const hasCallback = hash.access_token || hash.error || query.code || query.error
    if (!raw || !hasCallback) {
        return null
    }

    const pending = JSON.parse(raw)
    sessionStorage.removeItem(PENDING_KEY)
    const returnedState = hash.state || query.state
    cleanUrl()

    if (hash.error || query.error) {
        throw new Error(hash.error_description || query.error_description || hash.error || query.error)
    }
    if (returnedState !== pending.state) {
        throw new Error('OAuth state mismatch (possible CSRF)')
    }

    const cfg = PROVIDERS[pending.provider]
    let accessToken
    if (cfg.responseType === 'token') {
        accessToken = hash.access_token
    } else {
        const { data } = await axios.post(cfg.codeExchangeUrl(), {
            code: query.code,
            redirectUri: REDIRECT_URI(),
        })
        accessToken = data && data.access_token
    }
    if (!accessToken) {
        throw new Error(`${pending.provider}: no access_token in callback`)
    }

    return {
        provider: pending.provider,
        access_token: accessToken,
        intent: pending.intent,
        returnTo: pending.returnTo,
        extra: pending.extra,
    }
}

export default {
    install(app) {
        app.config.globalProperties.$auth = { authenticate, handleRedirectResult }
    },
}
